import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    ExternalLink, ShieldCheck, ShieldAlert, Clock, Ban, FileDown, Sheet,
    LineChart, Landmark, Layers, Search, Loader2, ArrowRight, TrendingUp, Timer,
    Send, MessageSquare, CheckCircle2, AlertTriangle,
    Eye, Files,
} from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
    Tabs, TabsList, TabsTrigger, TabsContent,
} from "../../components/ui/tabs";
import AnalyticsTab from "./tabs/AnalyticsTab";

function money(n, cur = "MXN") {
    if (n === null || n === undefined) return "—";
    return Number(n).toLocaleString("es-MX", {
        style: "currency", currency: cur, maximumFractionDigits: 0,
    });
}

const EVENT_ICON = {
    borrowing_base: TrendingUp,
    reporte: FileDown,
    covenant: ShieldAlert,
    springing_trigger: Ban,
    snapshot: Layers,
};
const EVENT_COLOR = {
    borrowing_base: "text-[color:var(--brand-dark-gold)]",
    reporte: "text-foreground",
    covenant: "text-[color:var(--brand-coral)]",
    springing_trigger: "text-[color:var(--brand-coral)]",
    snapshot: "text-[color:var(--brand-exito)]",
};
const REQ_TIPOS = [
    { value: "informacion_adicional", label: "Información adicional" },
    { value: "inspeccion_libros", label: "Inspección de libros" },
    { value: "reporte_ad_hoc", label: "Reporte ad-hoc" },
    { value: "otro", label: "Otro" },
];
const ESTADO_BADGE = {
    pendiente: "badge-gold",
    en_proceso: "badge-gold",
    respondida: "badge-exito",
    vencida: "badge-error",
};

// Countdown: horas restantes al SLA
function slaCountdown(fecha_limite) {
    if (!fecha_limite) return null;
    try {
        const limite = new Date(fecha_limite.replace("Z", "+00:00"));
        const ahora = new Date();
        const diff_h = (limite - ahora) / (1000 * 60 * 60);
        return diff_h;
    } catch {
        return null;
    }
}

function SLABadge({ req }) {
    const h = slaCountdown(req.fecha_limite_respuesta);
    if (req.estado === "respondida") {
        return (
            <span className={req.sla_cumplido ? "badge-exito" : "badge-error"}>
                {req.sla_cumplido ? "SLA cumplido" : "SLA incumplido"}
            </span>
        );
    }
    if (req.estado === "vencida" || (h !== null && h < 0)) {
        return <span className="badge-error">Vencida ({Math.abs(Math.round(h || 0))}h)</span>;
    }
    if (h === null) return null;
    const color = h < 12 ? "badge-error" : h < 24 ? "badge-gold" : "badge-exito";
    return <span className={color}>Faltan {Math.round(h)}h</span>;
}

function NewRequestModal({ facilityId, onCreated }) {
    const [open, setOpen] = useState(false);
    const [tipo, setTipo] = useState("informacion_adicional");
    const [descripcion, setDescripcion] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!descripcion || descripcion.length < 5) {
            toast.error("La descripción debe tener al menos 5 caracteres");
            return;
        }
        setSaving(true);
        try {
            await api.post("/portal/agent-requests", {
                facility_id: facilityId, tipo, descripcion,
            });
            toast.success("Solicitud enviada", {
                description: "El fiduciario tiene 48h hábiles para responder.",
            });
            setOpen(false); setDescripcion("");
            onCreated?.();
        } catch (err) {
            toast.error("Error creando solicitud", {
                description: err?.response?.data?.detail || "Verifica los datos",
            });
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button data-testid="portal-new-request-btn">
                    <Send className="h-4 w-4 mr-2" /> Nueva solicitud
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Nueva solicitud al Fiduciario/Agente</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <p className="text-xs text-muted-foreground">
                        El fiduciario tiene un SLA de 48h para responder. Toda la trazabilidad
                        queda registrada en bitácora inmutable.
                    </p>
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Tipo
                        </label>
                        <Select value={tipo} onValueChange={setTipo}>
                            <SelectTrigger className="mt-1" data-testid="req-tipo-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {REQ_TIPOS.map((t) => (
                                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Descripción detallada
                        </label>
                        <textarea
                            value={descripcion} onChange={(e) => setDescripcion(e.target.value)}
                            className="mt-1 w-full min-h-[120px] rounded-md border border-border bg-background p-2 text-sm"
                            placeholder="Ej. Solicito el detalle de collections diarias del último mes desagregado por sector."
                            data-testid="req-descripcion-input"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
                        Cancelar
                    </Button>
                    <Button onClick={submit} disabled={saving} data-testid="req-submit-btn">
                        {saving ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enviando…</>)
                                : (<><Send className="h-4 w-4 mr-2" /> Enviar solicitud</>)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function PortalExterno() {
    const { user } = useAuth();
    const [facilities, setFacilities] = useState([]);
    const [selFac, setSelFac] = useState(null);
    const [timeline, setTimeline] = useState([]);
    const [reportes, setReportes] = useState([]);
    const [requests, setRequests] = useState([]);
    const [dataRoomAccesos, setDataRoomAccesos] = useState({ shares: [], accesos: [] });
    const [loading, setLoading] = useState(true);
    const [folio, setFolio] = useState("");
    const [verifyResult, setVerifyResult] = useState(null);
    const [verifyOpen, setVerifyOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/portal/facilities");
            setFacilities(data);
            if (data.length > 0) setSelFac((prev) => prev || data[0]);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Sin acceso al portal");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const loadTabs = async () => {
        if (!selFac) return;
        try {
            const [t, r, req, dr] = await Promise.all([
                api.get(`/portal/facilities/${selFac.id}/timeline`),
                api.get(`/portal/facilities/${selFac.id}/reportes`).catch(() => ({ data: [] })),
                api.get("/portal/agent-requests"),
                api.get(`/portal/facilities/${selFac.id}/data-room-accesos`)
                    .catch(() => ({ data: { shares: [], accesos: [] } })),
            ]);
            setTimeline(t.data);
            setReportes(r.data);
            setRequests(req.data);
            setDataRoomAccesos(dr.data || { shares: [], accesos: [] });
        } catch {}
    };
    useEffect(() => { loadTabs(); /* eslint-disable-next-line */ }, [selFac]);

    // Requests filtradas por facility seleccionada
    const facReqs = useMemo(
        () => requests.filter((r) => r.facility_id === selFac?.id),
        [requests, selFac],
    );

    const download = async (id, kind, periodo) => {
        try {
            const r = await api.get(`/portal/reportes/${id}/${kind}`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `reporte_${selFac.numero}_${periodo}.${kind}`;
            a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error("Error descargando"); }
    };

    const verificar = async () => {
        try {
            const { data } = await api.get(`/verificar/${folio.toUpperCase().trim()}`);
            setVerifyResult(data);
        } catch (err) {
            setVerifyResult({ error: err?.response?.data?.detail || "Folio no encontrado" });
        }
    };

    // Semáforo agregado del facility seleccionado
    const semaforo = selFac?.covenants_status || {};

    if (loading) {
        return (
            <div className="p-10 flex items-center gap-3 text-muted-foreground" data-testid="portal-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> Cargando portal…
            </div>
        );
    }

    if (facilities.length === 0) {
        return (
            <div className="p-10" data-testid="portal-empty">
                <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                    Portal Externo
                </div>
                <h1 className="font-display text-4xl">Bienvenida, {user?.nombre?.split(" ")[0]}</h1>
                <div className="mt-6 rounded-md border border-dashed border-border p-10 text-center">
                    <p className="text-sm text-muted-foreground">
                        No hay facilities visibles para tu perfil. Contacta al Administrador Fiduciario.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-8 lg:p-10 max-w-[1600px] space-y-6" data-testid="portal-page">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Portal · Solo lectura
                    </div>
                    <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                        <ExternalLink className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                        Portal Lender
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Vista consolidada de tus facilities · reportes verificables · timeline de eventos ·
                        solicitudes al fiduciario con SLA 48h.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Dialog open={verifyOpen} onOpenChange={setVerifyOpen}>
                        <DialogTrigger asChild>
                            <Button variant="outline" data-testid="portal-verify-folio-btn">
                                <Search className="h-4 w-4 mr-2" /> Verificar folio
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Verificar folio público</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3">
                                <p className="text-xs text-muted-foreground">
                                    Ingresa el folio de un documento (instrucción, reporte, acuse) para
                                    verificar su autenticidad contra la bitácora inmutable.
                                </p>
                                <Input placeholder="Folio de 16 caracteres" value={folio}
                                    onChange={(e) => setFolio(e.target.value)}
                                    data-testid="portal-folio-input" />
                                <Button className="w-full" onClick={verificar}
                                    data-testid="portal-verificar-btn">
                                    <ShieldCheck className="h-4 w-4 mr-2" /> Verificar
                                </Button>
                                {verifyResult && (
                                    <pre className="text-xs bg-muted p-3 rounded-sm max-h-64 overflow-x-auto" data-testid="portal-verify-result">
                                        {JSON.stringify(verifyResult, null, 2)}
                                    </pre>
                                )}
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* Selector de facility si >1 */}
            {facilities.length > 1 && (
                <div className="flex items-center gap-3">
                    <span className="text-[11px] uppercase tracking-widest text-muted-foreground">Facility</span>
                    <Select value={selFac?.id} onValueChange={(v) => setSelFac(facilities.find((f) => f.id === v))}>
                        <SelectTrigger className="w-96" data-testid="portal-facility-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {facilities.map((f) => (
                                <SelectItem key={f.id} value={f.id}>
                                    {f.numero} · {f.nombre_comercial}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}

            {selFac && (
                <>
                    {/* KPIs siempre visibles */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="portal-kpis">
                        <div className="kpi-card">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                                <Landmark className="h-3 w-3" /> Comprometido
                            </div>
                            <div className="kpi-value mt-2 text-2xl">
                                {money(selFac.monto_comprometido, selFac.moneda)}
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-1">{selFac.moneda} · {selFac.tipo}</div>
                        </div>
                        <div className="kpi-card">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                                <LineChart className="h-3 w-3" /> Dispuesto
                            </div>
                            <div className="kpi-value mt-2 text-2xl">
                                {money(selFac.monto_dispuesto, selFac.moneda)}
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-1">
                                {Number(selFac.bb_actual?.utilizacion_pct || 0).toFixed(1)}% utilización
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                                <Layers className="h-3 w-3" /> BB neto
                            </div>
                            <div className="kpi-value mt-2 text-2xl">
                                {money(selFac.bb_actual?.borrowing_base_neto, selFac.moneda)}
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-1">
                                Corte {selFac.bb_actual?.fecha_corte || "—"}
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Cushion</div>
                            <div className={`kpi-value mt-2 text-2xl ${Number(selFac.bb_actual?.cushion || 0) < 0 ? "text-[color:var(--brand-coral)]" : "text-[color:var(--brand-exito)]"}`}>
                                {money(selFac.bb_actual?.cushion, selFac.moneda)}
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <Tabs defaultValue="panorama" className="w-full">
                        <TabsList data-testid="portal-tabs">
                            <TabsTrigger value="panorama" data-testid="tab-panorama">Panorama</TabsTrigger>
                            <TabsTrigger value="reportes" data-testid="tab-reportes">Reportes</TabsTrigger>
                            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
                            <TabsTrigger value="solicitudes" data-testid="tab-solicitudes">
                                Solicitudes ({facReqs.filter((r) => r.estado !== "respondida").length})
                            </TabsTrigger>
                        </TabsList>

                        {/* ===== Tab Panorama ===== */}
                        <TabsContent value="panorama" className="mt-4">
                            {/* Semáforo covenants */}
                            <div className="rounded-md border border-border bg-card p-5" data-testid="portal-covenants-status">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-display text-xl">Estado de covenants</h3>
                                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                        Última medición
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {[
                                        { key: "cumple", label: "Cumple", color: "text-[color:var(--brand-exito)]", Icon: ShieldCheck },
                                        { key: "en_cure_period", label: "En cure period", color: "text-[color:var(--brand-dark-gold)]", Icon: Clock },
                                        { key: "breach", label: "Breach", color: "text-[color:var(--brand-coral)]", Icon: ShieldAlert },
                                        { key: "waived", label: "Waived", color: "text-muted-foreground", Icon: Ban },
                                    ].map((k) => (
                                        <div key={k.key} className="rounded-sm border border-border p-3">
                                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                                                <k.Icon className={`h-3 w-3 ${k.color}`} /> {k.label}
                                            </div>
                                            <div className={`font-display text-2xl mt-1 ${k.color}`}>{semaforo[k.key] || 0}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Timeline */}
                            <div className="rounded-md border border-border bg-card p-5 mt-6" data-testid="portal-timeline">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-display text-xl flex items-center gap-2">
                                        <Timer className="h-4 w-4 text-[color:var(--brand-dark-gold)]" /> Timeline de eventos
                                    </h3>
                                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                        Últimos 80
                                    </span>
                                </div>
                                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                                    {timeline.length === 0 && (
                                        <div className="text-sm text-muted-foreground py-6 text-center">
                                            Sin eventos recientes.
                                        </div>
                                    )}
                                    {timeline.map((e, i) => {
                                        const Icon = EVENT_ICON[e.tipo] || ArrowRight;
                                        const color = EVENT_COLOR[e.tipo] || "text-foreground";
                                        return (
                                            <div key={i} className="flex items-start gap-3 py-2 border-b border-border/40" data-testid={`timeline-event-${i}`}>
                                                <div className={`mt-0.5 ${color}`}>
                                                    <Icon className="h-4 w-4" />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="text-xs tabular text-muted-foreground">
                                                        {e.fecha?.slice(0, 16)}
                                                    </div>
                                                    <div className="text-sm">{e.detalle}</div>
                                                </div>
                                                <span className="badge-gold">{e.tipo}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </TabsContent>

                        {/* ===== Tab Reportes ===== */}
                        <TabsContent value="reportes" className="mt-4">
                            <div className="rounded-md border border-border bg-card p-5" data-testid="portal-reportes">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-display text-xl">Reportes disponibles</h3>
                                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                        Firmados y enviados
                                    </span>
                                </div>
                                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                                    {reportes.length === 0 && (
                                        <div className="text-sm text-muted-foreground py-6 text-center">
                                            Sin reportes disponibles aún.
                                        </div>
                                    )}
                                    {reportes.map((r) => (
                                        <div key={r.id} className="flex items-center gap-3 py-2 border-b border-border/40" data-testid={`portal-reporte-${r.periodo}`}>
                                            <div className="flex-1">
                                                <div className="text-sm font-medium">Periodo {r.periodo}</div>
                                                <div className="text-[11px] text-muted-foreground flex gap-2">
                                                    <span>{r.idioma}</span>
                                                    <span>·</span>
                                                    <span>{r.generado_at?.slice(0, 10)}</span>
                                                    <span>·</span>
                                                    {r.firmado ? (
                                                        <span className="text-[color:var(--brand-exito)]">Firmado</span>
                                                    ) : (
                                                        <span className="text-[color:var(--brand-coral)]">DRAFT</span>
                                                    )}
                                                </div>
                                            </div>
                                            <Button size="sm" variant="ghost"
                                                onClick={() => download(r.id, "pdf", r.periodo)}
                                                data-testid={`portal-dl-pdf-${r.periodo}`}>
                                                <FileDown className="h-3 w-3 mr-1" /> PDF
                                            </Button>
                                            <Button size="sm" variant="ghost"
                                                onClick={() => download(r.id, "xlsx", r.periodo)}
                                                data-testid={`portal-dl-xlsx-${r.periodo}`}>
                                                <Sheet className="h-3 w-3 mr-1" /> XLSX
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </TabsContent>

                        {/* ===== Tab Analytics ===== */}
                        <TabsContent value="analytics" className="mt-4">
                            <AnalyticsTab facility={selFac} readOnly />
                            {/* Widget: Últimos accesos Data Room */}
                            <div className="rounded-md border border-border bg-card p-5 mt-6" data-testid="portal-data-room-widget">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-display text-xl flex items-center gap-2">
                                        <Files className="h-4 w-4 text-[color:var(--brand-dark-gold)]" />
                                        Últimos accesos a Data Room
                                    </h3>
                                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                        {dataRoomAccesos.shares?.length || 0} shares · {dataRoomAccesos.accesos?.length || 0} accesos
                                    </span>
                                </div>
                                {(!dataRoomAccesos.accesos || dataRoomAccesos.accesos.length === 0) ? (
                                    <div className="text-sm text-muted-foreground py-6 text-center">
                                        Sin accesos registrados aún.
                                    </div>
                                ) : (
                                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                                        {dataRoomAccesos.accesos.slice(0, 15).map((a, i) => (
                                            <div key={i} className="flex items-start gap-3 py-2 border-b border-border/40"
                                                data-testid={`portal-dr-acceso-${i}`}>
                                                <Eye className="h-3 w-3 text-[color:var(--brand-dark-gold)] mt-1" />
                                                <div className="flex-1">
                                                    <div className="text-sm truncate">
                                                        {a.documento_nombre}
                                                    </div>
                                                    <div className="text-[10px] tabular text-muted-foreground flex gap-2 flex-wrap">
                                                        <span>{a.accedido_at?.slice(0, 16)?.replace("T", " ")}</span>
                                                        <span>·</span>
                                                        <span>{a.viewer_email || "sin viewer"}</span>
                                                        <span>·</span>
                                                        <span>{a.ip}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </TabsContent>



                        {/* ===== Tab Solicitudes ===== */}
                        <TabsContent value="solicitudes" className="mt-4">
                            <div className="rounded-md border border-border bg-card p-5" data-testid="portal-solicitudes">
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <h3 className="font-display text-xl flex items-center gap-2">
                                            <MessageSquare className="h-4 w-4 text-[color:var(--brand-dark-gold)]" />
                                            Solicitudes al Agente
                                        </h3>
                                        <p className="text-[11px] text-muted-foreground mt-0.5">
                                            SLA de 48h · trazabilidad en bitácora inmutable
                                        </p>
                                    </div>
                                    <NewRequestModal facilityId={selFac.id} onCreated={loadTabs} />
                                </div>

                                {facReqs.length === 0 && (
                                    <div className="text-sm text-muted-foreground py-10 text-center">
                                        Sin solicitudes registradas para esta facility.
                                    </div>
                                )}

                                <div className="space-y-3">
                                    {facReqs.map((r) => (
                                        <div key={r.id} className="rounded-md border border-border p-4"
                                            data-testid={`portal-request-${r.id}`}>
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="badge-gold">{r.tipo.replace("_", " ")}</span>
                                                        <span className={ESTADO_BADGE[r.estado] || "badge-gold"}>
                                                            {r.estado}
                                                        </span>
                                                        <SLABadge req={r} />
                                                    </div>
                                                    <div className="text-sm mt-2">{r.descripcion}</div>
                                                    <div className="text-[10px] text-muted-foreground mt-2 tabular">
                                                        Solicitada {r.fecha_solicitud?.slice(0, 16)?.replace("T", " ")}
                                                        {r.fecha_limite_respuesta && (
                                                            <> · Límite {r.fecha_limite_respuesta?.slice(0, 16)?.replace("T", " ")}</>
                                                        )}
                                                    </div>
                                                    {r.respuesta && (
                                                        <div className="mt-3 pt-3 border-t border-border/60">
                                                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                                                                <CheckCircle2 className="h-3 w-3 text-[color:var(--brand-exito)]" />
                                                                Respuesta {r.fecha_respuesta?.slice(0, 16)?.replace("T", " ")}
                                                            </div>
                                                            <div className="text-sm mt-1">{r.respuesta}</div>
                                                        </div>
                                                    )}
                                                    {r.estado === "vencida" && (
                                                        <div className="mt-2 flex items-center gap-1 text-[11px] text-[color:var(--brand-coral)]">
                                                            <AlertTriangle className="h-3 w-3" />
                                                            SLA incumplido · escalar con administrador
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </TabsContent>
                    </Tabs>
                </>
            )}
        </div>
    );
}
